import PropTypes from 'prop-types';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
// import Banner from 'sections/banners/banner';
// material-ui
import AppBar from '@mui/material/AppBar';
import { useTheme } from '@mui/material/styles';
import {
  useMediaQuery,
  Box,
  Button,
  Container,
  Drawer,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useScrollTrigger
} from '@mui/material';

// project import
import { APP_DEFAULT_PATH } from 'config';
import IconButton from 'components/@extended/IconButton';
import AnimateButton from 'components/@extended/AnimateButton';
import Logo from 'components/logo';

// import useAuth from 'hooks/useAuth';

import { getAuth } from 'firebase/auth';

// assets
import { MenuOutlined, LineOutlined, HomeOutlined } from '@ant-design/icons';

// ==============================|| COMPONENTS - APP BAR ||============================== //

// elevation scroll
function ElevationScroll({ layout, children, window }) {
  const theme = useTheme();
  // const theme = useTheme();

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 10,
    target: window ? window() : undefined
  });

  const backColorScroll = theme.palette.mode === 'dark' ? theme.palette.grey[50] : theme.palette.grey[50];
  const backColor = layout !== 'landing' ? backColorScroll : 'transparent';

  return React.cloneElement(children, {
    style: {
      backgroundColor: trigger ? backColorScroll : backColor
    }
  });
}

const Header = ({ handleDrawerOpen, layout = 'landing', ...others }) => {
  const theme = useTheme();
  const auth = getAuth();
  const location = useLocation();

  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
  // const matchDownLG = useMediaQuery(theme.breakpoints.down('lg'));

  const [drawerToggle, setDrawerToggle] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const isHomePage = location.pathname === '/' || location.pathname === '';

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsLoggedIn(!!user); // !!user converts user to a boolean value
    });

    // Cleanup function to unsubscribe from the auth state listener
    return () => unsubscribe();
  }, []); // Empty dependency array means this effect runs once after the initial render

  // const {
  //   // firebaseFacebookSignIn,
  //   firebaseGoogleSignIn,
  //   // firebaseTwitterSignIn
  // } = useAuth();

  const navigate = useNavigate();
  const googleHandler = async () => {
    try {
      // await firebaseGoogleSignIn();
      navigate('/lp-analysis');
    } catch (err) {
      console.error(err);
    }
  };

  /** Method called on multiple components with different event types */
  const drawerToggler = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerToggle(open);
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const commonLinkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    px: 1.5,
    py: 0.75,
    borderRadius: 2,
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.2s',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
      bgcolor: theme.palette.action.hover
    }
  };

  const activeLinkStyle = {
    ...commonLinkStyle,
    color: theme.palette.primary.main,
    bgcolor: 'transparent', // Remove background for active state
    fontWeight: 700, // Use bold text instead
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '4px',
      left: '12px',
      right: '12px',
      height: '2px',
      backgroundColor: theme.palette.primary.main,
      borderRadius: '1px'
    }
  };

  return (
    <ElevationScroll layout={layout} {...others}>
      <AppBar sx={{ bgcolor: 'transparent', color: theme.palette.text.primary, boxShadow: 'none' }}>
        {/* <Banner /> */}
        <Container maxWidth="xl" disableGutters={matchDownMd}>
          <Toolbar sx={{ width: '100%', px: { xs: 1.5, md: 0, lg: 0 }, py: 2 }}>
            <Stack direction="row" sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} alignItems="center">
              <Typography component="div" sx={{ textAlign: 'left', display: 'inline-block' }}>
                <Logo reverse to="/" />
              </Typography>
            </Stack>
            <Stack direction="row" sx={{ display: { xs: 'none', md: 'flex' } }} spacing={1} alignItems="center">
              {!isHomePage && (
                <Link
                  component={RouterLink}
                  to="/"
                  underline="none"
                  sx={{
                    ...commonLinkStyle,
                    color: 'text.primary'
                  }}
                >
                  Home
                </Link>
              )}
              <Link
                component={RouterLink}
                to="/ai"
                underline="none"
                sx={isActive('/ai') ? activeLinkStyle : { ...commonLinkStyle, color: 'text.primary' }}
              >
                Marketing agent
              </Link>
              <Link
                component={RouterLink}
                to="/interviews-landing"
                underline="none"
                sx={isActive('/interviews-landing') ? activeLinkStyle : { ...commonLinkStyle, color: 'text.primary' }}
              >
                User Interviews
              </Link>
              <Link
                component={RouterLink}
                to="/pricing"
                underline="none"
                sx={isActive('/pricing') ? activeLinkStyle : { ...commonLinkStyle, color: 'text.primary' }}
              >
                Pricing
              </Link>
              <Box sx={{ display: 'inline-block' }}>
                {!isLoggedIn ? (
                  <AnimateButton>
                    <Button
                      component={Link}
                      onClick={googleHandler}
                      // onClick={() => navigate('/login')}
                      disableElevation
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(90deg, #3b82f6 0%, #9333ea 100%)',
                        textTransform: 'none',
                        // fontSize: '1rem',
                        // fontWeight: 500,
                        boxShadow: '0 4px 12px rgba(59,130,246,0.25)',
                        '&:hover': {
                          background: 'linear-gradient(90deg, #2563eb 0%, #7e22ce 100%)',
                          boxShadow: '0 6px 16px rgba(59,130,246,0.35)'
                        }
                      }}
                    >
                      Sign In
                    </Button>
                  </AnimateButton>
                ) : (
                  <AnimateButton>
                    <Button
                      component={Link}
                      href="/agent"
                      disableElevation
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(90deg, #3b82f6 0%, #9333ea 100%)',
                        textTransform: 'none',
                        // fontSize: '1rem',
                        // fontWeight: 500,
                        boxShadow: '0 4px 12px rgba(59,130,246,0.25)',
                        '&:hover': {
                          background: 'linear-gradient(90deg, #2563eb 0%, #7e22ce 100%)',
                          boxShadow: '0 6px 16px rgba(59,130,246,0.35)'
                        }
                      }}
                    >
                      Go To Platform
                    </Button>
                  </AnimateButton>
                )}
              </Box>
            </Stack>
            <Box
              sx={{
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                display: { xs: 'flex', md: 'none' }
              }}
            >
              <Typography component="div" sx={{ textAlign: 'left', display: 'inline-block' }}>
                <Logo reverse to="/" />
              </Typography>
              <Stack direction="row" spacing={2}>
                {layout === 'component' && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    component={RouterLink}
                    to={APP_DEFAULT_PATH}
                    sx={{ mt: 0.5, height: 28 }}
                  >
                    Dashboard
                  </Button>
                )}

                <IconButton
                  color="secondary"
                  {...(layout === 'component' ? { onClick: handleDrawerOpen } : { onClick: drawerToggler(true) })}
                  sx={{
                    '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'secondary' : 'secondary.dark' }
                  }}
                >
                  <MenuOutlined style={{ color: theme.palette.mode === 'dark' ? 'inherit' : theme.palette.grey[900] }} />
                </IconButton>
              </Stack>
              <Drawer
                anchor="top"
                open={drawerToggle}
                onClose={drawerToggler(false)}
                sx={{ '& .MuiDrawer-paper': { backgroundImage: 'none' } }}
              >
                <Box
                  sx={{
                    width: 'auto',
                    '& .MuiListItemIcon-root': {
                      fontSize: '1rem',
                      minWidth: 28
                    }
                  }}
                  role="presentation"
                  onClick={drawerToggler(false)}
                  onKeyDown={drawerToggler(false)}
                >
                  <List>
                    {isLoggedIn ? (
                      <Link style={{ textDecoration: 'none' }} href="/agent">
                        <ListItemButton component="span">
                          <ListItemIcon>
                            <LineOutlined />
                          </ListItemIcon>
                          <ListItemText primary="Go to platform" primaryTypographyProps={{ variant: 'h6', color: 'text.primary' }} />
                        </ListItemButton>
                      </Link>
                    ) : (
                      <Link style={{ textDecoration: 'none' }} href="/login" target="_blank">
                        <ListItemButton component="span">
                          <ListItemIcon>
                            <LineOutlined />
                          </ListItemIcon>
                          <ListItemText primary="Sign In" primaryTypographyProps={{ variant: 'h6', color: 'text.primary' }} />
                        </ListItemButton>
                      </Link>
                    )}

                    {!isHomePage && (
                      <Link style={{ textDecoration: 'none' }} component={RouterLink} to="/">
                        <ListItemButton component="span">
                          <ListItemIcon>
                            <HomeOutlined />
                          </ListItemIcon>
                          <ListItemText primary="Home" primaryTypographyProps={{ variant: 'h6', color: 'text.primary' }} />
                        </ListItemButton>
                      </Link>
                    )}
                    <Link style={{ textDecoration: 'none' }} component={RouterLink} to="/ai">
                      <ListItemButton component="span" selected={isActive('/ai')}>
                        <ListItemIcon>
                          <LineOutlined style={{ color: isActive('/ai') ? theme.palette.primary.main : 'inherit' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Marketing agent"
                          primaryTypographyProps={{
                            variant: 'h6',
                            color: isActive('/ai') ? 'primary.main' : 'text.primary'
                            // fontWeight: isActive('/ai') ? 400 : 400
                          }}
                        />
                      </ListItemButton>
                    </Link>
                    <Link style={{ textDecoration: 'none' }} component={RouterLink} to="/pricing">
                      <ListItemButton component="span" selected={isActive('/pricing')}>
                        <ListItemIcon>
                          <LineOutlined style={{ color: isActive('/pricing') ? theme.palette.primary.main : 'inherit' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Pricing"
                          primaryTypographyProps={{
                            variant: 'h6',
                            color: isActive('/pricing') ? 'primary.main' : 'text.primary',
                            fontWeight: isActive('/pricing') ? 600 : 400
                          }}
                        />
                      </ListItemButton>
                    </Link>
                    <Link style={{ textDecoration: 'none' }} component={RouterLink} to="/interviews-landing">
                      <ListItemButton component="span" selected={isActive('/interviews-landing')}>
                        <ListItemIcon>
                          <LineOutlined style={{ color: isActive('/interviews-landing') ? theme.palette.primary.main : 'inherit' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="User Interviews"
                          primaryTypographyProps={{
                            variant: 'h6',
                            color: isActive('/interviews-landing') ? 'primary.main' : 'text.primary',
                            fontWeight: isActive('/interviews-landing') ? 600 : 400
                          }}
                        />
                      </ListItemButton>
                    </Link>
                    {/* <Link style={{ textDecoration: 'none' }} href="https://blog.askpot.com" target="_blank">
                      <ListItemButton component="span">
                        <ListItemIcon>
                          <LineOutlined />
                        </ListItemIcon>
                        <ListItemText primary="Blog" primaryTypographyProps={{ variant: 'h6', color: 'text.primary' }} />
                      </ListItemButton>
                    </Link> */}
                  </List>
                </Box>
              </Drawer>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </ElevationScroll>
  );
};

Header.propTypes = {
  handleDrawerOpen: PropTypes.func,
  layout: PropTypes.string
};

export default Header;
